import { useTranslation } from 'react-i18next';

import { AttributeItem } from './AttributeItem';

import { useAuth } from '@/contexts/AuthContext';
import { useGetTaxonomyCurrentPropertiesByCategoryName } from '@/hooks/useGetTaxonomyCurrentPropertiesByCategoryName';
import { capitalize } from '@/lib/utils';
import { VisionProductAttributes } from '@/services/ProductsService';

const hiddenAttributes = [
  'seo_content',
  'seo_content_default',
  'itens_details',
  'items_details',
  'pairs_with',
];

const prioritizedKeys = [
  'design_features',
  'prints_and_colors',
  'sales_support',
];

interface VisionDescriptionProps {
  productId: number;
  visionCategory: string;
  attributes: VisionProductAttributes;
}

export function Attributes({
  productId,
  visionCategory,
  attributes,
}: VisionDescriptionProps) {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'pages.VisionProduct.components.Attribute',
  });

  const { user } = useAuth();

  const customerLanguage = user?.customer.language
    .split('-')
    .shift()
    ?.toLowerCase();

  const attributesKeyTexts = {
    designFeatures: t('designFeatures', {
      lng: customerLanguage,
      fallbackLng: i18n.language,
    }),
    printsAndColors: t('printsAndColors', {
      lng: customerLanguage,
      fallbackLng: i18n.language,
    }),
    salesSupport: t('salesSupport', {
      lng: customerLanguage,
      fallbackLng: i18n.language,
    }),
    voiceTone: t('voiceTone', {
      lng: customerLanguage,
      fallbackLng: i18n.language,
    }),
  };

  const { taxonomies } =
    useGetTaxonomyCurrentPropertiesByCategoryName(visionCategory);

  if (!attributes) {
    return null;
  }

  const sortedAttributes = Object.entries(attributes)
    .filter(([key]) => !hiddenAttributes.includes(key))
    .map(([key, value]) => {
      const priority = prioritizedKeys.findIndex((pk) => pk === key);

      return {
        key,
        order: priority === -1 ? 999 : priority,
        value,
      };
    })
    .sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-8">
      {sortedAttributes.map(({ key, value }) => {
        if (hiddenAttributes.includes(key)) {
          return null;
        }

        const sortedValues = [...value].sort((a, b) => {
          if (a.order === undefined && b.order === undefined) return 0;
          if (a.order === undefined) return 1;
          if (b.order === undefined) return -1;
          return a.order - b.order;
        });

        if (sortedValues.length === 0) {
          return null;
        }

        return (
          <section key={key}>
            <header className="flex h-[54px] items-center bg-[#F4F4F4] px-5">
              <h2 className="text-sm font-bold">
                {Object.entries(attributesKeyTexts).find(
                  ([attrKey]) =>
                    attrKey.toLowerCase() ===
                    key.toLowerCase().replaceAll?.('_', ''),
                )?.[1] ?? capitalize(key?.replaceAll?.('_', ' '))}
              </h2>
            </header>

            <dl>
              {sortedValues.map((item) => (
                <AttributeItem
                  key={item.id}
                  productId={productId}
                  attributeId={item.id}
                  attributeProperty={item?.property}
                  attributeName={item.name}
                  propertyKey={key}
                  taxonomies={taxonomies}
                />
              ))}
            </dl>
          </section>
        );
      })}
    </div>
  );
}
